import React from "react";
import { classNames } from "primereact/utils";
import "./SelectableCard.scss";

export type SelectableCardProps = {
    selected?: boolean;
    disabled?: boolean;
    icon?: string;
    title: React.ReactNode;
    description?: React.ReactNode;
    onClick?: () => void;
    className?: string;
    "aria-label"?: string;
};

export const SelectableCard: React.FC<SelectableCardProps> = ({
    selected = false,
    disabled = false,
    icon,
    title,
    description,
    onClick,
    className,
    "aria-label": ariaLabel,
}) => (
    <button
        type="button"
        className={classNames(
            "selectable-card",
            selected && "selectable-card--selected",
            disabled && "selectable-card--disabled",
            className
        )}
        onClick={onClick}
        disabled={disabled}
        aria-pressed={selected}
        aria-label={ariaLabel}
    >
        {icon ? <i className={classNames("selectable-card__icon", icon)} aria-hidden /> : null}
        <span className="selectable-card__content">
            <span className="selectable-card__title">{title}</span>
            {description ? <span className="selectable-card__description">{description}</span> : null}
        </span>
    </button>
);

export type SelectableCardOption<T extends string = string> = {
    value: T;
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: string;
    disabled?: boolean;
};

export type SelectableCardGroupProps<T extends string = string> = {
    options: SelectableCardOption<T>[];
    value: T;
    onChange: (value: T) => void;
    columns?: 1 | 2 | 3;
    className?: string;
    cardClassName?: string;
    name?: string;
};

export function SelectableCardGroup<T extends string = string>({
    options,
    value,
    onChange,
    columns = 2,
    className,
    cardClassName,
    name,
}: SelectableCardGroupProps<T>) {
    return (
        <div
            className={classNames("selectable-card-group", `selectable-card-group--cols-${columns}`, className)}
            role="radiogroup"
            aria-label={name}
        >
            {options.map((option) => (
                <SelectableCard
                    key={option.value}
                    selected={value === option.value}
                    disabled={option.disabled}
                    icon={option.icon}
                    title={option.title}
                    description={option.description}
                    className={cardClassName}
                    aria-label={typeof option.title === "string" ? option.title : option.value}
                    onClick={() => onChange(option.value)}
                />
            ))}
        </div>
    );
}
